import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { ReactComponent as Copy } from "../../assets/imgs/copy.svg";
import { ReactComponent as Copied } from "../../assets/imgs/copied.svg";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box, IconButton, TablePagination, Tooltip, useTheme } from "@mui/material";

import t from "../../helpers/en";

import Search from "../Inputs/SearchInput";
import CollapsibleTable from "./CollapsibleTable";
import Loading from "../Loading";

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.03,
      duration: 0.2,
      ease: "easeOut",
    },
  }),
  exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
};

export default function BasicTable(props) {
  const {
    rows,
    actionBtns,
    subActionBtns,
    selectedRow,
    searchedValue,
    onChangeSearchTxt,
    onClearSearchTxt,
    onKeyPressSearch,
    page,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
    collapsible,
    onCollapse,
    isLoading,
    hideTableHeader,
    specificDetails,
    loadingItems,
    onRowClick,
  } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Theme-responsive colors
  const tableColors = {
    bg: isDarkMode ? "#1e293b" : "#ffffff",
    headerBg: isDarkMode
      ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
      : "linear-gradient(135deg, #9A3412 0%, #F97316 100%)",
    border: isDarkMode ? "#334155" : "#e2e8f0",
    rowEven: isDarkMode ? "#1e293b" : "#fff",
    rowOdd: isDarkMode ? "#0f172a" : "#f8fafc",
    text: isDarkMode ? "#e2e8f0" : "#334155",
    textMuted: isDarkMode ? "#94a3b8" : "#64748b",
    // Subtle hover - slightly darker/lighter than row background
    hoverBg: isDarkMode ? "rgba(234, 88, 12, 0.15)" : "rgba(234, 88, 12, 0.05)",
    hoverText: isDarkMode ? "#e2e8f0" : "#1e293b",
    hoverLink: "#F97316",
    toolbarBg: isDarkMode ? "#0f172a" : "#f8fafc",
  };

  const [copied, setCopied] = useState({ status: false });

  const copy2Clipboard = async (el) => {
    try {
      await navigator.clipboard.writeText(el.value);
      setCopied((prevState) => ({
        ...prevState,
        ...el,
        status: true,
      }));
    } catch {}
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "12px",
        boxShadow: isDarkMode ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.08)",
        border: `1px solid ${tableColors.border}`,
        overflow: "hidden",
        background: tableColors.bg,
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}
    >
      {!hideTableHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: `1px solid ${tableColors.border}`,
            background: tableColors.toolbarBg,
          }}
        >
          <TablePagination
            sx={{
              "& .MuiToolbar-root": { padding: "0", minHeight: "auto" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
                color: tableColors.textMuted,
              },
              "& .MuiTablePagination-select, & .MuiSelect-icon": {
                color: tableColors.text,
              },
            }}
            component="div"
            count={rows?.totalElementsForPagination ?? rows.bodyCount?.length}
            {...{ page, rowsPerPage }}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
          {onChangeSearchTxt && (
            <Search
              className={{ marginLeft: "auto" }}
              {...{
                searchedValue,
                onChangeSearchTxt,
                onClearSearchTxt,
                onKeyPressSearch,
              }}
            />
          )}
        </Box>
      )}
      {specificDetails?.topElem}
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow
            sx={{
              background: tableColors.headerBg,
            }}
          >
            {rows.header.map((row, rowIdx) => (
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#ffffff",
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  py: 1.5,
                  borderBottom: "none",
                }}
                key={rowIdx}
              >
                {row}
              </TableCell>
            ))}
            {actionBtns && <TableCell sx={{ borderBottom: "none" }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          <AnimatePresence mode="popLayout">
            {rows.body.length > 0 ? (
              rows.body?.map((row, rowIdx) => {
                const isSelectedRow = selectedRow(row);

                return (
                  <React.Fragment key={row.id || rowIdx}>
                    <TableRow
                      component={motion.tr}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      custom={rowIdx}
                      layout
                      onClick={() => onRowClick && onRowClick(row)}
                      sx={{
                        ...(!row.deleted
                          ? {
                              "&:hover": {
                                backgroundColor: specificDetails?.disableHover
                                  ? "transparent"
                                  : tableColors.hoverBg,
                                color: specificDetails?.disableHover
                                  ? "inherit"
                                  : tableColors.hoverText,
                                cursor: specificDetails?.disableHover
                                  ? "default"
                                  : "pointer",
                                "& .MuiTableCell-root": {
                                  color: specificDetails?.disableHover
                                    ? "inherit"
                                    : tableColors.hoverText,
                                },
                                "& .MuiTableCell-root a": {
                                  color: specificDetails?.disableHover
                                    ? "#F97316"
                                    : tableColors.hoverLink,
                                },
                                "& .MuiTableCell-root svg": {
                                  fill: tableColors.hoverText,
                                },
                              },
                            }
                          : {}),
                        backgroundColor: isSelectedRow
                          ? tableColors.hoverBg
                          : rowIdx % 2 === 0
                          ? tableColors.rowEven
                          : tableColors.rowOdd,
                        color: row?.deleted ? "#ef4444" : tableColors.text,
                        opacity: row?.deleted ? 0.6 : 1,
                        "&:last-child td, &:last-child th": { border: 0 },
                        "& .MuiTableCell-root svg": {
                          fill: row?.deleted ? "#ef4444" : tableColors.textMuted,
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {rows?.bodyParams(row, rowIdx)?.map((it, itIdx) => {
                        let cellValue = it?.specialDetails?.hasTooltip ? (
                          <Tooltip title={it?.value} arrow>
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span
                                style={{
                                  ...(it?.specialDetails?.ellipsis ?? {}),
                                }}
                              >
                                {it?.specialDetails?.isUrl ? (
                                  <a
                                    href={it?.value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#F97316" }}
                                  >
                                    {it?.value}
                                  </a>
                                ) : (
                                  it?.value
                                )}
                              </span>
                              {it?.specialDetails?.ellipsis && it?.value && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    copy2Clipboard({
                                      value: it?.value,
                                      row,
                                      columnIdx: itIdx,
                                    })
                                  }
                                  sx={{
                                    ml: 0.5,
                                    "&:hover": { background: "rgba(234, 88, 12, 0.1)" },
                                  }}
                                >
                                  {copied.status &&
                                  JSON.stringify(copied.row) ===
                                    JSON.stringify(row) &&
                                  itIdx === copied.columnIdx ? (
                                    <Copied />
                                  ) : (
                                    <Copy />
                                  )}
                                </IconButton>
                              )}
                            </span>
                          </Tooltip>
                        ) : (
                          it?.value
                        );

                        if (it?.isCollapsible) {
                          cellValue = (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {cellValue}
                              {cellValue ? (
                                <IconButton
                                  size="small"
                                  onClick={() => onCollapse(row)}
                                  sx={{
                                    ml: 0.5,
                                    "&:hover": { background: "rgba(234, 88, 12, 0.1)" },
                                  }}
                                >
                                  {loadingItems[row.id] ? (
                                    <Loading
                                      specificDetails={{
                                        size: 20,
                                      }}
                                    />
                                  ) : collapsible[row.id] ? (
                                    <KeyboardArrowUpIcon sx={{ fontSize: "1.25rem" }} />
                                  ) : (
                                    <KeyboardArrowDownIcon sx={{ fontSize: "1.25rem" }} />
                                  )}
                                </IconButton>
                              ) : (
                                ""
                              )}
                            </span>
                          );
                        }

                        return (
                          <TableCell
                            sx={{
                              color: "inherit",
                              py: 1.5,
                              px: 2,
                              fontSize: "0.875rem",
                              borderBottom: `1px solid ${tableColors.border}`,
                              ...(it?.style ?? {}),
                            }}
                            key={itIdx}
                            component="th"
                            scope="row"
                          >
                            {cellValue}
                          </TableCell>
                        );
                      })}
                      {actionBtns && (
                        <TableCell
                          sx={{
                            borderBottom: `1px solid ${tableColors.border}`,
                            py: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "end", gap: 0.5 }}>
                            {actionBtns(row, rowIdx)}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                    {rows?.subBody &&
                      rows.subBody(row) &&
                      !loadingItems[row.id] && (
                        <CollapsibleTable
                          open={collapsible[row.id]}
                          data={rows.subBody(row)}
                          subheader={rows.subheader}
                          subbody={rows.subbodyParams}
                          parentElem={row}
                          subActionBtns={(val) => subActionBtns(val, row)}
                        />
                      )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow
                component={motion.tr}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                sx={{ background: tableColors.rowEven }}
              >
                <TableCell
                  sx={{
                    textAlign: "center",
                    py: 6,
                    color: tableColors.textMuted,
                    fontSize: "0.9rem",
                  }}
                  colSpan={rows?.header?.length + (actionBtns ? 1 : 0)}
                >
                  {!isLoading ? t.noResult : <Loading />}
                </TableCell>
              </TableRow>
            )}
          </AnimatePresence>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
